import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const cleanApiUrl = import.meta.env.VITE_API_URL?.replace(/[^a-zA-Z0-9:/._-]/g, '') || '';

type ConsentSubmitResponse = {
  message?: string;
};

type ConsentSubmitError = {
  status?: number;
  message?: string;
};

const getApiErrorMessage = (error: unknown): string => {
  const normalized = (error as ConsentSubmitError) || {};
  if (typeof normalized.message === 'string' && normalized.message.trim()) {
    return normalized.message;
  }

  if (normalized.status === 410) {
    return 'This consent link has expired. Please ask your nutritionist to resend a new link.';
  }

  if (normalized.status === 404) {
    return 'This client could not be found. Please contact your nutritionist.';
  }

  return 'Unable to submit consent right now. Please try again.';
};

const ClientConsentPage = () => {
  const [searchParams] = useSearchParams();
  const clientId = useMemo(() => (searchParams.get('clientId') || '').trim(), [searchParams]);
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  const [platformAccepted, setPlatformAccepted] = useState(false);
  const [dataProcessingAccepted, setDataProcessingAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const hasRequiredParams = Boolean(clientId && token);

  const handleSubmit = async () => {
    if (!hasRequiredParams) {
      setSubmitError('Invalid consent link. Missing client details or token.');
      return;
    }

    if (!platformAccepted || !dataProcessingAccepted) {
      setSubmitError('Please accept both consent checkboxes to continue.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const response = await fetch(`${cleanApiUrl}/clients/consent/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          token,
          platformAccepted: true,
          dataProcessingAccepted: true,
        }),
      });

      const payload = (await response.json().catch(() => ({}))) as ConsentSubmitResponse & { error?: string };

      if (!response.ok) {
        const messageFromPayload = typeof payload.error === 'string' ? payload.error : null;
        throw {
          status: response.status,
          message: messageFromPayload || 'Consent submission failed',
        } as ConsentSubmitError;
      }

      setSubmitSuccess(
        'Thank you! Your consent has been recorded successfully. Your practitioner can now continue providing your personalized nutrition care through CalorieScience.'
      );
    } catch (error) {
      setSubmitError(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#e9edf1] p-6 sm:p-8">
      <div className="mx-auto mt-8 w-full max-w-[680px] rounded-2xl border border-slate-200 bg-white p-8 sm:mt-14 sm:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0f7fa4]">CalorieScience</p>
        <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-800">Client Consent</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Please review the information below and provide your consent so your practitioner can use CalorieScience to deliver your personalized nutrition care.
        </p>

        {!hasRequiredParams && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Invalid consent link. Please use the latest email from your nutritionist.
          </div>
        )}

        {hasRequiredParams && (
          <div className="mt-8 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-5">
            <label className="flex items-start gap-3 text-sm leading-6 text-slate-700">
              <input
                type="checkbox"
                checked={platformAccepted}
                onChange={(event) => setPlatformAccepted(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#20AEDC] focus:ring-[#20AEDC]"
              />
              <span>
                <span className="block font-semibold text-slate-800">
                  I consent to using the CalorieScience platform
                </span>
                <span className="mt-1 block">
                  I understand that my practitioner will use CalorieScience to create, manage, and deliver my personalized nutrition program and related services.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 text-sm leading-6 text-slate-700">
              <input
                type="checkbox"
                checked={dataProcessingAccepted}
                onChange={(event) => setDataProcessingAccepted(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#20AEDC] focus:ring-[#20AEDC]"
              />
              <span>
                <span className="block font-semibold text-slate-800">
                  I consent to the collection and processing of my personal and health information
                </span>
                <span className="mt-1 block">
                  I authorize my practitioner and CalorieScience to securely collect, store, use, and process my personal information, health information, dietary information, biomarker data (where applicable), and related records for the purpose of providing personalized nutrition care and supporting my ongoing nutrition program.
                </span>
              </span>
            </label>

            <div className="border-t border-slate-200 pt-4 text-sm leading-6 text-slate-600">
              <p className="font-semibold text-slate-800">Your Privacy Matters</p>
              <p className="mt-2">
                Your information will be handled securely and processed in accordance with applicable UK/EU GDPR requirements. CalorieScience uses industry-standard security measures to help protect your personal information and privacy.
              </p>
              <p className="mt-3">
                By selecting "Submit Consent," you confirm that you have read and agree to the Privacy Policy and Terms of Use.
              </p>
            </div>
          </div>
        )}

        {submitError && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
            {submitSuccess}
          </div>
        )}

        <div className="mt-8">
          <Button
            type="button"
            disabled={!hasRequiredParams || isSubmitting || Boolean(submitSuccess)}
            onClick={handleSubmit}
            className="!h-11 !rounded-md !bg-[#20AEDC] !px-6 !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
          >
            {isSubmitting ? 'Submitting...' : submitSuccess ? 'Submitted' : 'Submit Consent'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientConsentPage;
